import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash, ShieldCheck, Key, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Disclaimer } from "@/components/Disclaimer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");

  const setPw = async () => {
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    await api.post("/auth/set-password", { password });
    setPassword("");
    await api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {});
    toast.success("Password updated.");
  };

  const exportData = async () => {
    const [profile, journal, reports] = await Promise.all([
      api.get("/profile").then((r) => r.data).catch(() => ({})),
      api.get("/journal").then((r) => r.data).catch(() => ({})),
      api.get("/results").then((r) => r.data).catch(() => ({})),
    ]);
    const blob = new Blob([JSON.stringify({ user, profile, journal, reports }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "theinner-my-data.json"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Your data has been exported.");
  };

  const deleteData = async () => {
    await api.delete("/profile/data");
    toast.success("All your profile data has been deleted.");
    navigate("/");
  };

  return (
    <div className="max-w-2xl">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Settings</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">Account & data</h1>

      <div className="mt-8 space-y-5">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="font-heading text-base font-medium text-slate-100">Profile</h2>
          <div className="mt-4 space-y-2 text-sm">
            <p className="text-slate-400"><span className="text-slate-500">Name:</span> {user?.name}</p>
            <p className="text-slate-400"><span className="text-slate-500">Email:</span> {user?.email}</p>
            <p className="text-slate-400"><span className="text-slate-500">Sign-in:</span> {user?.auth_provider === "google" ? "Google" : user?.has_password ? "Email & password" : "Email (passwordless lead)"}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="flex items-center gap-2 font-heading text-base font-medium text-slate-100"><Key size={18} weight="duotone" className="text-teal-400" /> {user?.has_password ? "Change password" : "Set a password"}</h2>
          <p className="mt-1 text-sm text-slate-500">{user?.has_password ? "Update your account password." : "Add a password so you can log in with email."}</p>
          <div className="mt-4 flex gap-3">
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 6)" className="flex-1 rounded-lg border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40" data-testid="settings-password-input" />
            <button onClick={setPw} className="rounded-full bg-teal-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="settings-set-password-btn">Save</button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="flex items-center gap-2 font-heading text-base font-medium text-slate-100"><ShieldCheck size={18} weight="duotone" className="text-teal-400" /> Your data, your control</h2>
          <p className="mt-1 text-sm text-slate-500">Export everything we store about you, or permanently delete your profile.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={exportData} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm font-medium text-slate-200 hover:border-slate-500" data-testid="settings-export-btn">
              <DownloadSimple size={16} /> Export my data
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="inline-flex items-center gap-2 rounded-full border border-red-500/30 px-5 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10" data-testid="settings-delete-btn">
                  <Trash size={16} /> Delete my data
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-slate-800 bg-slate-900">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-slate-100">Delete all your data?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    This permanently removes your profile, assessments, reports, journal entries, and coaching history. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteData} className="bg-red-500 text-white hover:bg-red-600" data-testid="settings-delete-confirm">Delete everything</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <button onClick={async () => { await logout(); navigate("/"); }} className="text-sm text-slate-500 hover:text-red-400" data-testid="settings-logout-btn">Sign out</button>

        <Disclaimer />
      </div>
    </div>
  );
}
